/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AnyAction } from "redux"
import { replace } from "lodash"
import { actionCreator } from "store/store"
import {
  Drawer,
  Divider,
  Button,
  Input,
  Row,
  Col,
  Switch,
  AutoComplete,
  Checkbox,
  DatePicker,
} from "@duxton-web/ui"
import {
  ChevronLeftUniversal,
  CloseCircleFilled,
  CloseUniversal,
} from "@duxton-web/icons"
import { Form } from "grab-ui"
import { days } from "utils/section_helper"
import {
  Availability,
  Timing,
  convertSellingTimeToSellingTimeFormValues,
  convertSellingTimeFormValuesToSellingTime,
  SellingTimeFormValues,
  createErrorCallback,
  updateErrorCallback,
  deleteErrorCallback,
  getTimingOptions,
  getSellingTimeEventInfo,
  defaultTiming,
} from "utils/selling_time_helper"
import { CreateSellingTimeResponse } from "types/api/menuCreateSellingTime"
import { AxiosAction } from "types/api"
import { AppState } from "store/store.type"

import { CheckableButtonGroup } from "components/common"
import { SellingTime } from "types/models/menu"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventCategory,
  SellingTimeEditType,
  SellingTimeSource,
} from "constants/tracking"
import { sellingTimeEditorDrawer } from "./styles"
import { drawerFooter } from "../styles"

const CheckboxGroup = Checkbox.Group

export interface SellingTimeEditorDrawerProps extends FormComponentProps {
  visible: boolean
  onClose?: () => void
}

export const SellingTimeEditorDrawerComponent: FC<SellingTimeEditorDrawerProps> = ({
  visible,
  onClose,
  form,
}: SellingTimeEditorDrawerProps) => {
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID, timeZone: menuGroupTimezone },
    outlet,
    outletV2,
    drawer: {
      sellingTime,
      showCategory,
      category: drawerCategory,
      showSubDepartment,
      showItemSelection,
      showItem,
      item: drawerItem,
      sourceShowItem,
      itemSelections,
    },
    isSellingTimeStructure,
    isMartDepartmentStructure,
    categories,
    subDepartments,
    items,
    enableTroyNewMerchantEndpoint,
  } = useSelector((state: AppState) => state.menu)

  const {
    getFieldDecorator,
    getFieldValue,
    getFieldsValue,
    setFieldsValue,
    isFieldsTouched,
    getFieldsError,
  } = form

  const dispatch = useDispatch()

  const textMap = useTranslationsMapTyped({
    createTitle: "menu.sellingTime.title.create",
    updateTitle: "menu.sellingTime.title.update",
    nameLabel: "menu.sellingTime.label.name",
    namePlaceholder: "menu.sellingTime.placeholder.name",
    availabilitiesLabel: "menu.sellingTime.label.availabilities",
    [`dayButton.${days[0]}`]: `menu.sellingTime.button.${days[0]}`,
    [`dayButton.${days[1]}`]: `menu.sellingTime.button.${days[1]}`,
    [`dayButton.${days[2]}`]: `menu.sellingTime.button.${days[2]}`,
    [`dayButton.${days[3]}`]: `menu.sellingTime.button.${days[3]}`,
    [`dayButton.${days[4]}`]: `menu.sellingTime.button.${days[4]}`,
    [`dayButton.${days[5]}`]: `menu.sellingTime.button.${days[5]}`,
    [`dayButton.${days[6]}`]: `menu.sellingTime.button.${days[6]}`,
    startTimeLabel: "menu.sellingTime.label.startTime",
    startTimePlaceholder: "menu.sellingTime.placeholder.startTime",
    endTimeLabel: "menu.sellingTime.label.endTime",
    endTimePlaceholder: "menu.sellingTime.placeholder.endTime",
    addTimingLink: "menu.sellingTime.link.addTiming",
    addAvailabilityButton: "menu.sellingTime.button.addAvailability",
    storeOpeningTime: `menu.sellingTime.label.storeOpeningTime`,
    storeClosingTime: `menu.sellingTime.label.storeClosingTime`,
    periodLabel: "menu.sellingTime.label.period",
    periodDescription: "menu.sellingTime.description.period",
    hasPeriodDescription: "menu.sellingTime.description.hasPeriod",
    requiredError: "menu.errors.requiredField",
    timingError: "menu.errors.invalidTiming",
    maxLengthError: "menu.errors.characterLimit",
    createButton: "menu.sellingTime.button.create",
    updateButton: "menu.sellingTime.button.update",
    deleteButton: "menu.sellingTime.button.delete",
    createSuccessMessage: "menu.sellingTime.toast.success.create",
    updateSuccessMessage: "menu.sellingTime.toast.success.update",
    deleteSuccessMessage: "menu.sellingTime.toast.success.delete",
    [`deleteModalTitle.none`]: "menu.sellingTime.modal.delete.title.none",
    [`deleteModalDescription.none`]: "menu.sellingTime.modal.delete.description.none",
    [`deleteModalTitle.item`]: "menu.sellingTime.modal.delete.title.item",
    [`deleteModalDescription.item`]: "menu.sellingTime.modal.delete.description.item",
    [`deleteModalTitle.category`]: "menu.sellingTime.modal.delete.title.category",
    [`deleteModalDescription.category`]: "menu.sellingTime.modal.delete.description.category",
    [`deleteModalTitle.itemCategory`]: "menu.sellingTime.modal.delete.title.itemCategory",
    [`deleteModalDescription.itemCategory`]: "menu.sellingTime.modal.delete.description.itemCategory",
    cancelButton: "menu.buttons.cancel",
    deleteModalConfirmButton: "menu.sellingTime.modal.delete.button.delete",
  })

  const outletTimezone = enableTroyNewMerchantEndpoint
    ? outletV2?.timezone
    : outlet?.timezone

  const timezone = menuGroupTimezone || outletTimezone

  const [availabilityIDs, setAvailabilityIDs] = useState<number[] | undefined>(
    undefined,
  )

  const [timingIDs, setTimingIDs] = useState<
    | {
        [availabilityID: string]: number[]
      }
    | undefined
  >(undefined)

  const [checkedDays, setCheckedDays] = useState<string[] | undefined>(
    undefined,
  )

  const [hasPeriod, setHasPeriod] = useState<boolean | undefined>(undefined)

  const [isTouched, setIsTouched] = useState(false)

  const [saveSellingTimeDisabled, setSaveSellingTimeDisabled] = useState(false)

  const [sellingTimeFormValues, setSellingTimeFormValues] = useState<
    SellingTimeFormValues | undefined
  >(undefined)

  const [isFormSetupDone, setIsFormSetupDone] = useState(false)

  const selectedItemIDs = Object.keys(itemSelections ?? {}).filter(
    itemID => itemSelections?.[itemID] === true,
  )

  const eventCategory =
    sourceShowItem === EventCategory.menuItemsModifyitem
      ? EventCategory.menuItemsModifySellingTime
      : EventCategory.menuOverviewModifySellingTime

  const hasCategory = showCategory && drawerCategory
  const hasItem = showItem && drawerItem
  const hasItems = showItemSelection && selectedItemIDs.length

  const getGeneralEventLabels = () => {
    const labels: { [key: string]: any } = {}

    // assign menu parent id
    if (merchantID) {
      labels.STORE_ID = merchantID
    } else if (menuGroupID) {
      labels.MENU_GROUP_ID = menuGroupID
    }

    // assign id and source
    if (hasCategory) {
      labels.CATEGORY_ID = drawerCategory?.categoryID || ""
      labels.SELLING_TIME_SOURCE = SellingTimeSource.categoryDetailEdit
    } else if (hasItem) {
      labels.ITEM_ID = drawerItem?.itemID || ""
      labels.SELLING_TIME_SOURCE = SellingTimeSource.categoryDetailEdit
    } else if (hasItems) {
      labels.ITEM_ID = selectedItemIDs
      labels.SELLING_TIME_SOURCE = SellingTimeSource.selectMultipleItem
      labels.NUM_ITEM = selectedItemIDs.length
    } else {
      labels.SELLING_TIME_SOURCE = SellingTimeSource.sellingTimeFilter
    }

    // assign edit type
    if (sellingTime) {
      labels.SELLING_TIME_EDIT_TYPE = SellingTimeEditType.editSellingTime
    } else {
      labels.SELLING_TIME_EDIT_TYPE = SellingTimeEditType.addSellingTime
    }
    return labels
  }

  useEffect(() => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.pageView,
        labels: {
          ...getGeneralEventLabels(),
        },
      })
    }
  }, [visible])

  useEffect(() => {
    if (sellingTime) {
      // start edit setup
      if (timezone) {
        setSellingTimeFormValues(
          convertSellingTimeToSellingTimeFormValues(sellingTime, timezone),
        )
      }
    } else {
      // start create setup
      setIsFormSetupDone(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingTime])

  // edit setup - update availabilityIDs to match current selling time
  useEffect(() => {
    if (sellingTimeFormValues && !isFormSetupDone) {
      const { availabilities } = sellingTimeFormValues
      const isClosedAllDay = !availabilities.length
      setAvailabilityIDs(isClosedAllDay ? [0] : availabilities.map((_, i) => i))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingTimeFormValues])

  // edit setup - update timingIDs to match current selling time
  useEffect(() => {
    if (sellingTimeFormValues && availabilityIDs && !isFormSetupDone) {
      const { availabilities } = sellingTimeFormValues
      const isClosedAllDay = !availabilities.length
      // update timingIDs to match total timings of current selling time
      if (isClosedAllDay) {
        setTimingIDs({ "0": [0] })
      } else {
        const newTimingIDs: {
          [availabilityID: string]: number[]
        } = {}

        availabilities.forEach((availability, i) => {
          newTimingIDs[i] = availability.timings.map(
            (_, timingIndex) => timingIndex,
          )
        })

        setTimingIDs(newTimingIDs)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityIDs])

  // edit setup - set fields value
  useEffect(() => {
    if (sellingTimeFormValues && timingIDs && !isFormSetupDone) {
      const { name, availabilities } = sellingTimeFormValues

      const availabilitiesFieldsValue: {
        [key: string]: any
      } = {}

      let newCheckedDays: string[] = []
      // assign field names and values with the format that setFieldsValue accept
      availabilities.forEach((availability, i) => {
        availabilitiesFieldsValue[`availabilities.${i}.days`] =
          availability.days
        newCheckedDays = [...newCheckedDays, ...availability.days]
        availability.timings.forEach((timing, j) => {
          const startTime =
            timing.startTime === defaultTiming.START
              ? textMap.storeOpeningTime
              : timing.startTime
          const endTime =
            timing.endTime === defaultTiming.END
              ? textMap.storeClosingTime
              : timing.endTime

          availabilitiesFieldsValue[
            `availabilities.${i}.timings.${j}.startTime`
          ] = startTime
          availabilitiesFieldsValue[
            `availabilities.${i}.timings.${j}.endTime`
          ] = endTime
        })
      })

      setFieldsValue({
        name,
        ...availabilitiesFieldsValue,
      })

      setCheckedDays(newCheckedDays)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timingIDs])

  useEffect(() => {
    if (checkedDays) {
      // edit setup - update timingIDs to match current selling time
      if (!isFormSetupDone && sellingTimeFormValues) {
        const { period } = sellingTimeFormValues
        setHasPeriod(!!period)
      } else if (checkedDays.length === days.length) {
        // if all days checked, remove availabilities with no checked days.
        const availabilities: {
          [key: string]: { days: string[] }
        } = getFieldValue("availabilities") // for some reason this is an object
        const noCheckedDaysAvailabilityIDs: number[] = []
        Object.keys(availabilities).forEach(id => {
          if (!availabilities[id].days.length) {
            // get all availability with no checked days
            noCheckedDaysAvailabilityIDs.push(Number(id))
          }
        })
        // remove all availability with no checked days from availabilityIDs
        setAvailabilityIDs(
          availabilityIDs?.filter(
            availabilityID =>
              noCheckedDaysAvailabilityIDs.indexOf(availabilityID) === -1,
          ),
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedDays])

  // edit setup - update periods value to match current selling time
  useEffect(() => {
    if (hasPeriod !== undefined && !isFormSetupDone && sellingTimeFormValues) {
      if (hasPeriod) {
        const { period } = sellingTimeFormValues
        setFieldsValue({
          period,
        })
      }
      // complete form setup
      setIsFormSetupDone(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPeriod])

  useEffect(() => {
    // if create - assign default values to state
    if (isFormSetupDone && !sellingTime) {
      setAvailabilityIDs([0])
      setTimingIDs({
        0: [0],
      })
      setCheckedDays([])
      setHasPeriod(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormSetupDone])

  const handleChangeCheckedDays = (
    checkedValues: string[],
    prevCheckedValues: string[],
  ) => {
    if (checkedDays) {
      if (checkedValues.length > prevCheckedValues.length) {
        // if check - push checked value to checkedDays
        const checkedValue = checkedValues[checkedValues.length - 1]
        setCheckedDays([...checkedDays, checkedValue])
      } else {
        // if uncheck - remove unchecked value from checkedDays
        const uncheckedValue = prevCheckedValues.filter(
          value => checkedValues.indexOf(value) === -1,
        )[0]
        const newCheckedDays = [...checkedDays]
        newCheckedDays.splice(newCheckedDays.indexOf(uncheckedValue), 1)
        setCheckedDays(newCheckedDays)
      }
      if (!isTouched) setIsTouched(true)
    }
  }

  const getSaveSellingTimeDisabled = () => {
    const { name, availabilities } = getFieldsValue()

    const fieldsError = getFieldsError()

    const nameError = fieldsError.name
    const availabilitiesError: any = fieldsError.availabilities

    let result = false

    if (sellingTime && !isFieldsTouched() && !isTouched) {
      // if edit and form is not touched
      result = true
    } else if (!name || nameError) {
      // if name empty or error
      result = true
    } else {
      Object.values(availabilities).some((availability: Availability) => {
        if (!availability.days.length) {
          // if no days are checked
          result = true
        } else {
          const timings = availability.timings
          Object.values(timings).some((timing: Timing) => {
            if (!timing.startTime || !timing.endTime) {
              // if timing empty
              result = true
            }
            return result
          })
        }
        return result
      })
      Object.values(availabilitiesError).some(
        (availabilityError: {
          timings: {
            startTime?: string[]
            endTime?: string[]
          }[]
        }) => {
          const timingsError = availabilityError.timings
          Object.values(timingsError).some(timingError => {
            if (timingError.startTime || timingError.endTime) {
              // if timing error
              result = true
            }
            return result
          })
          return result
        },
      )
    }
    return result
  }

  useEffect(() => {
    setSaveSellingTimeDisabled(getSaveSellingTimeDisabled())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, timingIDs, availabilityIDs, hasPeriod])

  const handleOnSuccess = () => {
    dispatch(
      actionCreator.menu[
        isMartDepartmentStructure ? "fetchCatalogue" : "fetchMenu"
      ]({
        merchantID,
        merchantGroupID,
        menuGroupID,
        isSellingTimeStructure,
      }),
    )
  }

  const handleClickSaveSellingTime = async () => {
    if (!timezone) {
      return
    }
    const convertedSellingTimeFormValues = convertSellingTimeFormValuesToSellingTime(
      // @ts-ignore
      form.getFieldsValue(),
      {
        startTime: textMap.storeOpeningTime,
        endTime: textMap.storeClosingTime,
      },
      timezone,
    )
    let labels: { [key: string]: any } = {
      ...getGeneralEventLabels(),
      SELLING_TIME_NAME: convertedSellingTimeFormValues.name,
      SERVICE_HOURS: convertedSellingTimeFormValues.serviceHours,
      TOGGLE_ACTION: hasPeriod ? "on" : "off",
      ...(hasPeriod && {
        PERIOD_DATE: {
          startTime: convertedSellingTimeFormValues.startTime,
          endTime: convertedSellingTimeFormValues.endTime,
        },
      }),
      SCHEDULE_COUNT: availabilityIDs?.length || 0,
      REASON: "",
    }
    if (sellingTime) {
      // edit
      const { sellingTimeID } = sellingTime
      labels.SELLING_TIME_ID = sellingTimeID
      const resp = (await dispatch(
        actionCreator.menu.updateSellingTime({
          sellingTimeID,
          data: {
            sellingTime: {
              sellingTimeID,
              merchantID,
              menuGroupID,
              ...convertedSellingTimeFormValues,
            },
          },
          merchantID,
          merchantGroupID,
          menuGroupID,
          successMessage: textMap.updateSuccessMessage,
          errorCallback: updateErrorCallback,
        }) as AnyAction,
      )) as AxiosAction<CreateSellingTimeResponse>

      if (resp?.type.endsWith("_FAIL")) {
        labels = {
          ...labels,
          ...getResponseErrorEventLabels(resp),
        }
      } else if (resp?.type.endsWith("_SUCCESS")) {
        handleOnSuccess()
      }
    } else {
      // create
      const isOnCategoryItemDrawer =
        showCategory || showSubDepartment || showItem
      const resp = (await dispatch(
        actionCreator.menu.createSellingTime({
          data: {
            sellingTime: {
              merchantID,
              menuGroupID,
              ...convertedSellingTimeFormValues,
            },
          },
          merchantID,
          merchantGroupID,
          menuGroupID,
          successMessage: textMap.createSuccessMessage,
          closeDrawers: !isOnCategoryItemDrawer,
          errorCallback: createErrorCallback,
        }) as AnyAction,
      )) as AxiosAction<CreateSellingTimeResponse>

      if (resp?.type.endsWith("_FAIL")) {
        labels = {
          ...labels,
          ...getResponseErrorEventLabels(resp),
        }
      } else if (resp?.type.endsWith("_SUCCESS")) {
        if (isOnCategoryItemDrawer) {
          dispatch(
            actionCreator.menu.toggleDrawer({
              showSellingTimeEditor: false,
              createdSellingTimeID: resp.payload.data.sellingTimeID,
            }),
          )
        }
        handleOnSuccess()
      }
    }
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.applySellingTime,
      labels,
    })
  }

  const getDeleteConfirmMessages = (deletedSellingTime: SellingTime) => {
    const categoryCount = isMartDepartmentStructure
      ? subDepartments.filter(
          subDepartment =>
            subDepartment.sellingTimeID === deletedSellingTime.sellingTimeID,
        ).length
      : categories.filter(
          category =>
            category.sellingTimeID === deletedSellingTime.sellingTimeID,
        ).length
    const itemCount = items.filter(
      item => item.sellingTimeID === deletedSellingTime.sellingTimeID,
    ).length
    let title
    let content
    if (categoryCount && itemCount) {
      title = textMap["deleteModalTitle.itemCategory"]
        .replace("<x>", `${itemCount}`)
        .replace("<y>", `${categoryCount}`)
      content = textMap["deleteModalDescription.itemCategory"]
    } else if (itemCount) {
      title = textMap["deleteModalTitle.item"].replace("<x>", `${itemCount}`)
      content = textMap["deleteModalDescription.item"]
    } else if (categoryCount) {
      title = textMap["deleteModalTitle.category"].replace(
        "<x>",
        `${categoryCount}`,
      )
      content = textMap["deleteModalDescription.category"]
    } else {
      title = textMap["deleteModalTitle.none"]
      content = textMap["deleteModalDescription.none"]
    }
    return {
      title,
      content,
    }
  }

  const handleClickDeleteSellingTime = async () => {
    // if edit - delete current selling time
    if (sellingTime) {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.clickRemoveSellingTime,
        labels: {
          ...getGeneralEventLabels(),
        },
      })
      const { sellingTimeID } = sellingTime
      const confirmOptions = {
        ...getDeleteConfirmMessages(sellingTime),
        okText: textMap.deleteModalConfirmButton,
        cancelText: textMap.cancelButton,
      }

      const resp = (await dispatch(
        actionCreator.menu.deleteSellingTime({
          sellingTimeID,
          merchantID,
          merchantGroupID,
          menuGroupID,
          confirmOptions,
          successMessage: textMap.deleteSuccessMessage,
          errorCallback: deleteErrorCallback,
        }) as AnyAction,
      )) as AxiosAction<CreateSellingTimeResponse>

      if (resp) {
        // on ok
        let labels: { [key: string]: any } = {
          ...getGeneralEventLabels(),
          ...getSellingTimeEventInfo(sellingTime),
          REASON: "",
          ERROR_MSG: "",
        }

        if (resp?.type.endsWith("_FAIL")) {
          labels = {
            ...labels,
            ...getResponseErrorEventLabels(resp),
          }
        }

        if (resp?.type.endsWith("_SUCCESS")) {
          handleOnSuccess()
        }

        TroyWebTracker.sendEvent({
          category: eventCategory,
          action: EventAction.removeSellingTime,
          labels,
        })
      } else {
        // on cancel
        TroyWebTracker.sendEvent({
          category: eventCategory,
          action: EventAction.cancelRemoveSellingTime,
          labels: {
            ...getGeneralEventLabels(),
          },
        })
      }
    }
  }

  const handleOnClose = () => {
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.clickExit,
      labels: {
        ...getGeneralEventLabels(),
      },
    })
    dispatch(
      actionCreator.menu.toggleDrawer({
        showSellingTimeEditor: false,
      }),
    )
    onClose?.()
  }

  if (!availabilityIDs || !timingIDs || !timezone) {
    return null
  }

  const timingValidator = (rule: any, value: string, callback: any) => {
    if (
      !value ||
      value === textMap.storeOpeningTime ||
      value === textMap.storeClosingTime
    ) {
      callback()
    } else if (/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(value)) {
      callback()
    } else {
      callback(rule)
    }
  }

  const renderTiming = (
    availabilityID: number,
    id: number,
    index: number,
    timingIDsLength: number,
    onRemoveTimingID: (removedTimingID: number) => void,
  ) => (
    <Fragment key={id}>
      {index === 0 && (
        <Row gutter={20}>
          <Col span={timingIDsLength <= 1 ? 11 : 10}>
            <Form.Item label={textMap.startTimeLabel} />
          </Col>
          <Col span={2} />
          <Col span={timingIDsLength <= 1 ? 11 : 10}>
            <Form.Item label={textMap.endTimeLabel} />
          </Col>
        </Row>
      )}
      <Row key={id} gutter={20} align="middle">
        <Col span={timingIDsLength <= 1 ? 11 : 10}>
          <Form.Item className="time-input-form-item">
            {getFieldDecorator(
              `availabilities.${availabilityID}.timings.${id}.startTime`,
              {
                rules: [
                  {
                    required: true,
                    message: textMap.requiredError,
                  },
                  {
                    validator: timingValidator,
                    message: textMap.timingError,
                  },
                ],
              },
            )(
              <AutoComplete
                data-testid="timing-start-input"
                showSearch
                placeholder={textMap.startTimePlaceholder}
                className="time-input"
                options={getTimingOptions(
                  {
                    value: textMap.storeOpeningTime,
                  },
                  getFieldValue(
                    `availabilities.${availabilityID}.timings.${id}.startTime`,
                  ),
                )}
              />,
            )}
          </Form.Item>
        </Col>
        <Col span={2}>
          <Divider />
        </Col>
        <Col span={timingIDsLength <= 1 ? 11 : 10}>
          <Form.Item className="time-input-form-item">
            {getFieldDecorator(
              `availabilities.${availabilityID}.timings.${id}.endTime`,
              {
                rules: [
                  {
                    required: true,
                    message: textMap.requiredError,
                  },
                  {
                    validator: timingValidator,
                    message: textMap.timingError,
                  },
                ],
              },
            )(
              <AutoComplete
                data-testid="timing-end-input"
                showSearch
                placeholder={textMap.endTimePlaceholder}
                className="time-input"
                options={getTimingOptions(
                  {
                    value: textMap.storeClosingTime,
                  },
                  getFieldValue(
                    `availabilities.${availabilityID}.timings.${id}.endTime`,
                  ),
                )}
              />,
            )}
          </Form.Item>
        </Col>
        {timingIDsLength > 1 && (
          <Col span={2}>
            <span
              data-testid="remove-timing-button"
              css={css`
                cursor: pointer;
                color: #a3a3a3;
              `}
              onClick={() => onRemoveTimingID(id)}
            >
              <CloseCircleFilled />
            </span>
          </Col>
        )}
      </Row>
    </Fragment>
  )

  const renderAvailability = (id: number) => {
    const onRemoveTimingID = (removedTimingID: number) => {
      // remove timing by id
      setTimingIDs({
        ...timingIDs,
        [id]: timingIDs[id].filter(timingID => timingID !== removedTimingID),
      })
      if (!isTouched) setIsTouched(true)
    }

    return (
      <div
        key={id}
        className="form-section-container"
        css={css`
          margin-bottom: 12px;
        `}
      >
        {availabilityIDs.length > 1 && (
          <CloseUniversal
            data-testid="remove-availability-button"
            className="availability-close-icon"
            onClick={() => {
              if (checkedDays) {
                const removedCheckedDays = getFieldValue(
                  `availabilities.${id}.days`,
                )
                // remove availability by id
                setAvailabilityIDs(
                  availabilityIDs.filter(
                    availabilityID => availabilityID !== id,
                  ),
                )
                // remove checked days
                setCheckedDays(
                  checkedDays.filter(
                    checkedDay => removedCheckedDays.indexOf(checkedDay) === -1,
                  ),
                )
              }
            }}
          />
        )}
        <Form.Item
          css={css`
            display: none;
          `} // hide this input to use the value for CheckableButtonGroup
        >
          {getFieldDecorator(`availabilities.${id}.days`, { initialValue: [] })(
            <CheckboxGroup options={days} />,
          )}
        </Form.Item>
        <CheckableButtonGroup
          data-testid="availability-checkable-days-input"
          options={days.map(day => ({
            label: textMap[`dayButton.${day}`],
            value: day,
            // disable if day is not checked in this availability but is checked in other availability
            disabled:
              !checkedDays ||
              (getFieldValue(`availabilities.${id}.days`).indexOf(day) === -1 &&
                checkedDays.indexOf(day) > -1),
          }))}
          checkedValues={getFieldValue(`availabilities.${id}.days`) || []}
          onChange={checkedValues => {
            const fieldName = `availabilities.${id}.days`
            const prevCheckedValues = getFieldValue(`availabilities.${id}.days`)
            setFieldsValue({
              [fieldName]: checkedValues,
            })
            handleChangeCheckedDays(checkedValues, prevCheckedValues)
          }}
        />
        {timingIDs[id]?.map((timingID, i) =>
          renderTiming(id, timingID, i, timingIDs[id].length, onRemoveTimingID),
        )}
        <Button
          data-testid="add-timing-button"
          type="link"
          disabled={timingIDs[id]?.length >= 3}
          onClick={() => {
            // add new timing id
            const lastID = timingIDs[id][timingIDs[id].length - 1]
            setTimingIDs({
              ...timingIDs,
              [id]: [...timingIDs[id], lastID + 1],
            })
          }}
        >
          {textMap.addTimingLink}
        </Button>
      </div>
    )
  }

  return (
    <Drawer
      data-testid="drawer"
      css={sellingTimeEditorDrawer}
      title={sellingTime ? textMap.updateTitle : textMap.createTitle}
      width={500}
      open={visible}
      onClose={handleOnClose}
      destroyOnClose
      closeIcon={<ChevronLeftUniversal />}
    >
      <div>
        <Row className="formContainer">
          <div
            css={css`
              margin-bottom: 14px;
              .ant-form-item-required:after {
                display: none;
              }
            `}
          >
            <Form.Item label={textMap.nameLabel}>
              {getFieldDecorator("name", {
                rules: [
                  {
                    required: true,
                    message: textMap.requiredError,
                  },
                  {
                    max: 30,
                    message: replace(textMap.maxLengthError, "$limit", "30"),
                  },
                ],
              })(
                <Input
                  data-testid="name-input"
                  placeholder={textMap.namePlaceholder}
                />,
              )}
            </Form.Item>
          </div>
          <p
            css={css`
              font-weight: 500;
            `}
          >
            {textMap.availabilitiesLabel}
          </p>
          {availabilityIDs.map(id => renderAvailability(id))}
          <Button
            data-testid="add-availability-button"
            block
            css={css`
              font-weight: 500;
            `}
            disabled={
              checkedDays?.length === days.length || availabilityIDs.length >= 7
            }
            onClick={() => {
              // add new availability id
              const lastID = availabilityIDs[availabilityIDs.length - 1]
              setAvailabilityIDs([...availabilityIDs, lastID + 1])
              // add new timing ids for the new availability
              setTimingIDs({
                ...timingIDs,
                [lastID + 1]: [0],
              })
            }}
          >
            {textMap.addAvailabilityButton}
          </Button>
          <p
            css={css`
              font-weight: 500;
              margin-top: 24px;
            `}
          >
            {textMap.periodLabel}
          </p>
          <div
            className="form-section-container"
            css={css`
              margin-bottom: 64px;
            `}
          >
            <Row justify="space-between">
              <Col>
                {hasPeriod
                  ? textMap.hasPeriodDescription
                  : textMap.periodDescription}
              </Col>
              <Col>
                <Switch
                  data-testid="period-switch"
                  checked={hasPeriod}
                  onChange={value => {
                    setHasPeriod(value)
                    if (!isTouched) setIsTouched(true)
                  }}
                />
              </Col>
            </Row>
            {hasPeriod && (
              <Form.Item>
                {getFieldDecorator("period")(
                  <DatePicker.RangePicker
                    data-testid="period-input"
                    css={css`
                      width: 100%;
                      margin-top: 20px;
                    `}
                  />,
                )}
              </Form.Item>
            )}
          </div>
        </Row>
        <div css={drawerFooter}>
          <Row gutter={12}>
            {sellingTime && (
              <Col span={6}>
                <Button
                  data-testid="delete-button"
                  danger
                  block
                  onClick={handleClickDeleteSellingTime}
                >
                  {textMap.deleteButton}
                </Button>
              </Col>
            )}
            <Col flex="auto">
              <Button
                data-testid="save-button"
                type="primary"
                block
                disabled={saveSellingTimeDisabled}
                onClick={handleClickSaveSellingTime}
              >
                {sellingTime ? textMap.updateButton : textMap.createButton}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </Drawer>
  )
}
export const SellingTimeEditorDrawer = Form.create<
  SellingTimeEditorDrawerProps
>({})(SellingTimeEditorDrawerComponent)
